/**
 * Floral Fields Quick View Engine
 * High-performance, luxury modal for previewing product details,
 * browsing high-resolution galleries, choosing variants, and adding directly to cart.
 */
(function () {
  'use strict';

  const QuickView = {
    modal: null,
    body: null,
    currentProduct: null,
    selectedVariant: null,

    init: function () {
      this.modal = document.getElementById('ff-quickview-modal');
      this.body = document.getElementById('ff-quickview-body');
      if (!this.modal || !this.body) return;

      this.bindEvents();
    },

    bindEvents: function () {
      const self = this;

      // Click trigger (Capture phase to intercept before card link navigation)
      document.addEventListener('click', function (e) {
        const btn = e.target.closest('[data-quickview-btn]');
        if (btn) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          const handle = btn.getAttribute('data-product-handle');
          if (handle) {
            self.open(handle);
          }
          return;
        }

        // Close triggers
        if (e.target.closest('[data-quickview-close]')) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          self.close();
          return;
        }
      }, true);

      // ESC key to close
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.modal && !self.modal.hasAttribute('hidden')) {
          self.close();
        }
      });
    },

    open: function (handle) {
      if (!this.modal || !this.body) return;

      this.modal.removeAttribute('hidden');
      requestAnimationFrame(() => {
        this.modal.classList.add('is-open');
        document.body.classList.add('ff-modal-open');
      });

      // Loading state
      this.body.innerHTML = `
        <div class="ff-quickview-loading">
          <div class="ff-quickview-spinner"></div>
          <span>Loading product details...</span>
        </div>
      `;

      // Fetch product JSON
      fetch(`/products/${handle}.js`)
        .then(res => {
          if (!res.ok) throw new Error('Product not found');
          return res.json();
        })
        .then(product => {
          this.currentProduct = product;
          this.render(product);
        })
        .catch(err => {
          console.error('Quick view fetch error:', err);
          this.body.innerHTML = `
            <div class="ff-quickview-error">
              <p>Unable to load product preview.</p>
              <a href="/products/${handle}" class="ff-quickview-view-full">Go to Product Page &rarr;</a>
            </div>
          `;
        });
    },

    close: function () {
      if (!this.modal) return;
      this.modal.classList.remove('is-open');
      document.body.classList.remove('ff-modal-open');
      setTimeout(() => {
        this.modal.setAttribute('hidden', '');
        this.currentProduct = null;
        this.selectedVariant = null;
      }, 250);
    },

    render: function (product) {
      const self = this;
      this.selectedVariant = product.variants.find(v => v.available) || product.variants[0];

      const images = product.images && product.images.length > 0 ? product.images : [];
      const mainImg = this.selectedVariant.featured_image ? this.selectedVariant.featured_image.src : (images[0] || '');

      // Clean description excerpt (remove excessive HTML or limit length)
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = product.description || '';
      let descText = tempDiv.textContent || tempDiv.innerText || '';
      if (descText.length > 180) {
        descText = descText.substring(0, 180) + '...';
      }

      this.body.innerHTML = `
        <div class="ff-qv-layout">
          <!-- Left: Gallery -->
          <div class="ff-qv-gallery">
            <div class="ff-qv-main-img-wrap">
              <img id="ff-qv-main-img" src="${mainImg ? this.formatImg(mainImg, '800x') : ''}" alt="${this.escapeHtml(product.title)}" loading="eager">
            </div>
            ${images.length > 1 ? `
              <div class="ff-qv-thumbs">
                ${images.map((img, idx) => `
                  <button type="button" class="ff-qv-thumb-btn ${img === mainImg ? 'is-active' : ''}" data-thumb-src="${this.formatImg(img, '800x')}" aria-label="Image ${idx + 1}">
                    <img src="${this.formatImg(img, '120x')}" alt="${this.escapeHtml(product.title)} thumbnail" loading="lazy">
                  </button>
                `).join('')}
              </div>
            ` : ''}
          </div>

          <!-- Right: Details -->
          <div class="ff-qv-details">
            <div class="ff-qv-vendor">${this.escapeHtml(product.vendor || 'Floral Fields')}</div>
            <h3 class="ff-qv-title">
              <a href="${product.url}">${this.escapeHtml(product.title)}</a>
            </h3>

            <!-- Price Block -->
            <div class="ff-qv-price-wrap" id="ff-qv-price-block">
              ${this.renderPriceHtml(this.selectedVariant)}
            </div>

            <!-- Stock Status -->
            <div class="ff-qv-stock-wrap" id="ff-qv-stock-block">
              ${this.renderStockHtml(this.selectedVariant)}
            </div>

            ${descText ? `<p class="ff-qv-desc">${this.escapeHtml(descText)}</p>` : ''}

            <!-- Variant Selectors -->
            ${product.variants.length > 1 ? `
              <div class="ff-qv-options">
                ${product.options.map((opt, optIdx) => `
                  <div class="ff-qv-option-group" data-option-index="${optIdx}">
                    <label class="ff-qv-option-label">${this.escapeHtml(opt.name)}: <span class="ff-qv-option-val" id="ff-qv-val-${optIdx}">${this.escapeHtml(this.selectedVariant.options[optIdx])}</span></label>
                    <div class="ff-qv-option-values">
                      ${opt.values.map(val => {
                        const isSelected = this.selectedVariant.options[optIdx] === val;
                        return `
                          <button type="button" class="ff-qv-pill-btn ${isSelected ? 'is-selected' : ''}" data-option-index="${optIdx}" data-option-value="${this.escapeHtml(val)}">
                            ${this.escapeHtml(val)}
                          </button>
                        `;
                      }).join('')}
                    </div>
                  </div>
                `).join('')}
              </div>
            ` : ''}

            <!-- Quantity & Add to Cart -->
            <div class="ff-qv-atc-row">
              <div class="ff-qv-qty-selector">
                <button type="button" class="ff-qv-qty-btn" data-qty-change="-1" aria-label="Decrease quantity">&minus;</button>
                <input type="number" id="ff-qv-qty-input" class="ff-qv-qty-input" value="1" min="1" max="999" aria-label="Quantity">
                <button type="button" class="ff-qv-qty-btn" data-qty-change="1" aria-label="Increase quantity">&plus;</button>
              </div>

              <button type="button" id="ff-qv-submit-btn" class="ff-qv-atc-btn" ${!this.selectedVariant.available ? 'disabled' : ''}>
                <span>${this.selectedVariant.available ? 'ADD TO BASKET' : 'SOLD OUT'}</span>
              </button>
            </div>

            <!-- View Full Details Link -->
            <div class="ff-qv-footer-links">
              <a href="${product.url}" class="ff-qv-full-link">
                View Full Product Details &rarr;
              </a>
            </div>
          </div>
        </div>
      `;

      // Setup interaction listeners inside modal
      this.bindModalActions();
    },

    bindModalActions: function () {
      const self = this;
      const modalBody = this.body;

      // 1. Thumbnails click
      modalBody.querySelectorAll('.ff-qv-thumb-btn').forEach(btn => {
        btn.addEventListener('click', function () {
          modalBody.querySelectorAll('.ff-qv-thumb-btn').forEach(b => b.classList.remove('is-active'));
          this.classList.add('is-active');
          const bigImg = modalBody.querySelector('#ff-qv-main-img');
          if (bigImg) {
            bigImg.src = this.getAttribute('data-thumb-src');
          }
        });
      });

      // 2. Option pills click
      modalBody.querySelectorAll('.ff-qv-pill-btn').forEach(btn => {
        btn.addEventListener('click', function () {
          const optIdx = parseInt(this.getAttribute('data-option-index'));
          const optVal = this.getAttribute('data-option-value');

          const group = this.closest('.ff-qv-option-group');
          group.querySelectorAll('.ff-qv-pill-btn').forEach(b => b.classList.remove('is-selected'));
          this.classList.add('is-selected');

          const labelVal = group.querySelector(`#ff-qv-val-${optIdx}`);
          if (labelVal) labelVal.textContent = optVal;

          self.onOptionChange();
        });
      });

      // 3. Quantity steppers
      modalBody.querySelectorAll('.ff-qv-qty-btn').forEach(btn => {
        btn.addEventListener('click', function () {
          const delta = parseInt(this.getAttribute('data-qty-change'));
          const input = modalBody.querySelector('#ff-qv-qty-input');
          if (input) {
            let val = parseInt(input.value) || 1;
            val = Math.max(1, val + delta);
            input.value = val;
          }
        });
      });

      // 4. Add to cart submit
      const atcBtn = modalBody.querySelector('#ff-qv-submit-btn');
      if (atcBtn) {
        atcBtn.addEventListener('click', function () {
          self.addToCart();
        });
      }
    },

    onOptionChange: function () {
      if (!this.currentProduct) return;
      const modalBody = this.body;

      // Gather current selected options
      const currentSelectedOptions = [];
      modalBody.querySelectorAll('.ff-qv-option-group').forEach(group => {
        const selectedPill = group.querySelector('.ff-qv-pill-btn.is-selected');
        if (selectedPill) {
          currentSelectedOptions.push(selectedPill.getAttribute('data-option-value'));
        }
      });

      // Find matched variant
      const matchedVariant = this.currentProduct.variants.find(variant => {
        return variant.options.every((opt, i) => opt === currentSelectedOptions[i]);
      });

      if (matchedVariant) {
        this.selectedVariant = matchedVariant;

        // Update Price
        const priceBlock = modalBody.querySelector('#ff-qv-price-block');
        if (priceBlock) priceBlock.innerHTML = this.renderPriceHtml(matchedVariant);

        // Update Stock
        const stockBlock = modalBody.querySelector('#ff-qv-stock-block');
        if (stockBlock) stockBlock.innerHTML = this.renderStockHtml(matchedVariant);

        // Update Button
        const atcBtn = modalBody.querySelector('#ff-qv-submit-btn');
        if (atcBtn) {
          if (matchedVariant.available) {
            atcBtn.removeAttribute('disabled');
            atcBtn.querySelector('span').textContent = 'ADD TO BASKET';
          } else {
            atcBtn.setAttribute('disabled', 'disabled');
            atcBtn.querySelector('span').textContent = 'SOLD OUT';
          }
        }

        // Update image if variant has featured image
        if (matchedVariant.featured_image) {
          const bigImg = modalBody.querySelector('#ff-qv-main-img');
          const variantImgSrc = this.formatImg(matchedVariant.featured_image.src, '800x');
          if (bigImg) bigImg.src = variantImgSrc;

          // Highlight matched thumbnail
          modalBody.querySelectorAll('.ff-qv-thumb-btn').forEach(tb => {
            const isMatch = tb.getAttribute('data-thumb-src') === variantImgSrc;
            tb.classList.toggle('is-active', isMatch);
          });
        }
      }
    },

    addToCart: function () {
      if (!this.selectedVariant || !this.selectedVariant.available) return;

      const atcBtn = this.body.querySelector('#ff-qv-submit-btn');
      const qtyInput = this.body.querySelector('#ff-qv-qty-input');
      const quantity = qtyInput ? (parseInt(qtyInput.value) || 1) : 1;

      if (atcBtn) {
        atcBtn.setAttribute('disabled', 'disabled');
        atcBtn.classList.add('loading');
        atcBtn.querySelector('span').textContent = 'ADDING...';
      }

      const formData = new FormData();
      formData.append('id', this.selectedVariant.id);
      formData.append('quantity', quantity);
      formData.append('sections', 'cart-drawer,cart-icon-bubble');
      formData.append('sections_url', window.location.pathname);

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript, application/json'
        },
        body: formData
      })
        .then(res => {
          if (!res.ok) throw new Error('Add to cart failed');
          return res.json();
        })
        .then(response => {
          if (atcBtn) {
            atcBtn.classList.remove('loading');
            atcBtn.classList.add('success');
            atcBtn.querySelector('span').textContent = 'ADDED \u2713';
          }

          // Sync into cart drawer
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.renderContents === 'function' && response.sections) {
            cartDrawer.renderContents(response);
          } else if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }

          if (window.publish && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'ff-quickview',
              productVariantId: this.selectedVariant.id,
              cartData: response
            });
          }

          // Trigger cart count bubbles in header
          this.refreshCart();

          // Close modal after brief success feedback
          setTimeout(() => {
            this.close();
            if (cartDrawer && typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            }
          }, 450);
        })
        .catch(err => {
          console.error('Cart add error:', err);
          if (atcBtn) {
            atcBtn.removeAttribute('disabled');
            atcBtn.classList.remove('loading');
            atcBtn.querySelector('span').textContent = 'ADD TO BASKET';
            alert('Could not add to cart. Please try again.');
          }
        });
    },

    refreshCart: function () {
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => {
          // Update header cart bubbles
          document.querySelectorAll('.cart-count-bubble, #cart-icon-bubble .cart-count-bubble').forEach(bubble => {
            const countSpan = bubble.querySelector('span[aria-hidden="true"]');
            if (countSpan) countSpan.textContent = cart.item_count;
            if (cart.item_count > 0) {
              bubble.removeAttribute('hidden');
              bubble.style.display = 'flex';
            }
          });

          // Dispatch standard cart event
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        })
        .catch(e => console.error('Cart refresh error:', e));
    },

    renderPriceHtml: function (variant) {
      if (!variant) return '';
      const exVatPrice = (variant.price / 100).toFixed(2);
      const incVatPrice = ((variant.price * 1.20) / 100).toFixed(2);
      const isSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      const comparePrice = isSale ? (variant.compare_at_price / 100).toFixed(2) : null;

      return `
        <div class="ff-qv-price-row">
          <span class="ff-qv-price">&pound;${exVatPrice} <small class="ff-vat-note">ex VAT</small></span>
          ${comparePrice ? `<s class="ff-qv-compare-price">&pound;${comparePrice}</s>` : ''}
          <span class="ff-qv-tax-note">(&pound;${incVatPrice} inc VAT)</span>
        </div>
      `;
    },

    renderStockHtml: function (variant) {
      if (!variant) return '';
      if (variant.available) {
        return `
          <div class="ff-qv-stock ff-qv-stock--in">
            <span class="ff-qv-stock-dot"></span> In Stock & Ready to Ship
          </div>
        `;
      } else {
        return `
          <div class="ff-qv-stock ff-qv-stock--out">
            <span class="ff-qv-stock-dot"></span> Currently Out of Stock
          </div>
        `;
      }
    },

    formatImg: function (url, size) {
      if (!url) return '';
      return url.replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048)\./, '.')
                .replace(/\.(jpg|jpeg|gif|png|webp)/, `_${size}.$1`);
    },

    escapeHtml: function (str) {
      if (!str) return '';
      return str.replace(/[&<>'"]/g, tag => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#39;',
        '"': '&quot;'
      }[tag] || tag));
    }
  };

  // Init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => QuickView.init());
  } else {
    QuickView.init();
  }

  window.FFQuickView = QuickView;
})();
